// src/pages/onboardingMapping.ts
import type { PainEntry, PainSide } from '../components/Onboarding';

export type { PainSide };
export type PainKey = 'knee' | 'lowback' | 'neck' | 'hands' | 'saddle' | 'foot';

export interface PainItem { key: PainKey; side: PainSide | null; note: string }
export interface PainDetail { pain_free: boolean; items: PainItem[]; other: string }

const IN_PER_CM = 0.3937007874;
const LB_PER_KG = 2.2046226218;

// Display-only conversions, not storage-grade: height is kept to whole cm and
// weight to one decimal kg, so an imperial round-trip is not guaranteed lossless.

export function buildPainDetail(
  pains: Partial<Record<PainKey, PainEntry>>,
  painFree: boolean,
  painOther: string,
): PainDetail {
  const items: PainItem[] = (Object.keys(pains) as PainKey[]).map((key) => ({
    key,
    side: pains[key]!.side ?? null,
    note: pains[key]!.note ?? '',
  }));
  return { pain_free: painFree, items, other: painOther };
}

export function parsePainDetail(
  detail: PainDetail | null | undefined,
): { pains: Partial<Record<PainKey, PainEntry>>; painFree: boolean; painOther: string } {
  if (!detail) return { pains: {}, painFree: false, painOther: '' };
  const pains: Partial<Record<PainKey, PainEntry>> = {};
  for (const it of detail.items ?? []) {
    pains[it.key] = { side: (it.side ?? 'both') as PainSide, note: it.note ?? '' };
  }
  return { pains, painFree: !!detail.pain_free, painOther: detail.other ?? '' };
}

export function heightToCm(value: string, units: 'metric' | 'imperial'): number | undefined {
  const n = parseFloat(value);
  if (!value.trim() || Number.isNaN(n)) return undefined;
  return units === 'imperial' ? Math.round(n / IN_PER_CM) : Math.round(n);
}

export function weightToKg(value: string, units: 'metric' | 'imperial'): number | undefined {
  const n = parseFloat(value);
  if (!value.trim() || Number.isNaN(n)) return undefined;
  return units === 'imperial' ? Math.round((n / LB_PER_KG) * 10) / 10 : Math.round(n * 10) / 10;
}

export function cmToHeight(cm: number | null | undefined, units: 'metric' | 'imperial'): string {
  if (cm == null) return '';
  return units === 'imperial' ? String(Math.round(cm * IN_PER_CM)) : String(Math.round(cm));
}

export function kgToWeight(kg: number | null | undefined, units: 'metric' | 'imperial'): string {
  if (kg == null) return '';
  return units === 'imperial' ? String(Math.round(kg * LB_PER_KG)) : String(Math.round(kg * 10) / 10);
}
